import { useMemo } from "react";
import { TellescopeModelFromName, useTellescopeQuery } from "@tellescope/tanstack-query";
import { getDefaultView, isRealViewId } from "../components/ContactDetailsHeader/constants";

type View = TellescopeModelFromName<"custom_dashboards">;
type Blocks = View["blocks"];

/**
 * A contact-details view id resolved to the one thing every consumer actually needs to know: which
 * of three states it is in.
 *
 * The `loading` and `error` arms deliberately carry no `blocks`. A pending or failed read and a
 * genuinely empty view look identical if all are reported as an empty array, and a caller that writes
 * with `replaceObjectFields: true` turns that ambiguity into data loss — it replaces the saved blocks
 * with an array built on an empty base. Keeping `blocks` off those arms makes that mistake
 * unrepresentable rather than merely discouraged.
 *
 * `error` is distinct from `loading` because react-query reports a failed read as `isLoading: false`
 * with `data: undefined`. Folding it into `loading` leaves a caller rendering a spinner that never
 * resolves, so a transient failure looks like a permanent hang.
 */
export type ResolvedView =
  | { mode: "placeholder"; view: View; blocks: Blocks }
  | { mode: "saved"; viewId: string; view: View; blocks: Blocks }
  | { mode: "loading" }
  | { mode: "error" };

/**
 * Resolves a view id into its {@link ResolvedView} mode, so consumers switch on one tagged value
 * instead of each re-deriving placeholder-vs-saved from the id.
 *
 * The placeholder view is synthesized client-side by {@link getDefaultView} and has no server
 * record, so its query stays disabled and it is usable immediately — it never reports `loading`.
 *
 * @example
 * const resolved = useResolvedView(viewId);
 * if (resolved.mode === "loading") return <Spinner />;
 * if (resolved.mode === "error") return <FailedToLoad />;
 * // `resolved.blocks` is now known to be authoritative
 */
export const useResolvedView = (viewId: string): ResolvedView => {
  const isFetchable = isRealViewId(viewId);

  const {
    data: fetchedView,
    isLoading,
    isError,
  } = useTellescopeQuery({
    model: "custom_dashboards",
    method: "getOne",
    request: viewId,
    enabled: isFetchable,
  });

  return useMemo(() => {
    if (!isFetchable) {
      const view = getDefaultView();
      return { mode: "placeholder", view, blocks: view.blocks };
    }

    if (isError) return { mode: "error" };
    if (isLoading || !fetchedView) return { mode: "loading" };

    return { mode: "saved", viewId, view: fetchedView, blocks: fetchedView.blocks };
  }, [fetchedView, isError, isFetchable, isLoading, viewId]);
};
